import {
  type Region,
  getSettlementReportsHunterPartyPublicIdPath,
  getSettlementReportsMerchantPartyPublicIdPath,
} from "Infrastructure/Api/Api";
import { useMutation } from "@tanstack/react-query";
import { getApiUrl } from "Infrastructure/Api/Api";
import { JWT_KEY } from "Utils/AuthUtils";
import { getLocalStorageItem } from "Utils/Storage/LocalStorage/SecureLocalStorage";
import FileSaver from "file-saver";
import { AccessRoleCodes } from "Models/AccessRoleCodes";
import type { SettlementUserRole } from "Types/settlements";
import { Resources, useResource } from "Translations/Resources";

type Params = {
  partyPublicID: string;
  settlementPeriodID: number;
  userRole: SettlementUserRole;
  /** Market the report is scoped to. Required for hunter reports — a hunter can hold
   * licenses in multiple markets and each market settles in its own currency. */
  region?: Region;
};

const USER_ROLE_TO_DOWNLOAD_PATH: Record<
  SettlementUserRole,
  (partyPublicID: string) => string
> = {
  [AccessRoleCodes.Hunter]: getSettlementReportsHunterPartyPublicIdPath,
  [AccessRoleCodes.HunterPlus]: getSettlementReportsHunterPartyPublicIdPath,
  [AccessRoleCodes.Merchant]: getSettlementReportsMerchantPartyPublicIdPath,
};

const fetchSettlementReport = async ({
  partyPublicID,
  settlementPeriodID,
  userRole,
  region,
}: Params) => {
  const regionQuery = region ? `&region=${region}` : "";
  const route = `${getApiUrl()}${USER_ROLE_TO_DOWNLOAD_PATH[userRole](
    partyPublicID ?? "",
  )}?settlementPeriodID=${settlementPeriodID}${regionQuery}`;
  const response = await fetch(route, {
    method: "GET",
    mode: "cors",
    headers: { Authorization: getLocalStorageItem(JWT_KEY) ?? "" },
  });

  return response.clone();
};

//TODO: nice to have, unify with other file downloading hooks and create one general one that could be shared (useDownloadTerminalContract, partyIdentificationZip,...)
export const useDownloadSettlementReportMutation = () => {
  const { t } = useResource();

  return useMutation({
    mutationFn: async (params: Params) => {
      const response = await fetchSettlementReport(params);
      if (!response.ok) {
        throw new Error(
          t(
            Resources.Components.Settlements.SettlementList.DownloadReportError,
          ),
        );
      }
      const blob = await response.blob();
      const regionSuffix = params.region ? `_${params.region}` : "";
      const fileName = `${t(Resources.Components.Settlements.SettlementList.DownloadReportFilePrefix)}_${params.settlementPeriodID}${regionSuffix}.pdf`;
      FileSaver.saveAs(blob, fileName);
    },
  });
};
